import { ChallengeService } from '@duck/api-definitions'
import {
  CommandLineIcon,
  CheckCircleIcon,
  FunnelIcon
} from '@heroicons/react/24/outline'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import type { INotebookContent } from '@jupyterlab/nbformat'
import { useEffectAsync } from '../../hooks/useEffectAsync'
import { useAuth } from '@/hooks/useAuth'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { useShowJoinGamma } from '@/hooks/useShowJoinGamma'
import { useUserSettings } from '@/hooks/useUserSettings'
import { apis } from '../../modules/apiClients'
import { useNavigate } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { postSubmitMessageAtom } from '@/store/workspaceAtoms'
import { PostSubmitMessageDialog } from './PostSubmitMessageDialog'
import { trackAchievement, useTar } from '@/modules'
import { FeedbackOverview, CompletedChallenge } from '../FeedbackDialog'
import { NoDataEmptyState, LoginRequiredEmptyState } from '../EmptyStates'
import { CompletedSubmissionCard } from './CompletedSubmissionCard'
import { CompletedSubmissionCardSkeleton } from './CompletedSubmissionCardSkeleton'
import { WysiwygEditor } from '../WysiwygEditor/WysiwygEditor'
import { ReadOnlyPythonNotebook } from '../PythonNotebook/ReadOnlyPythonNotebook'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button, SinglePick } from '@duck/ui'

type AttemptFilter = 'first' | 'second' | 'third' | 'fourth' | 'last'

export const CompletedChallengesPage = () => {
  const [challenges, setChallenges] = useState<CompletedChallenge[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showLoading, setShowLoading] = useState(false)
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [selectedChallenge, setSelectedChallenge] = useState<CompletedChallenge | null>(null)
  const [initialFeedbackViewMode, setInitialFeedbackViewMode] = useState<'feedback' | 'submitFeedback'>('feedback')
  const [isSolutionDialogOpen, setIsSolutionDialogOpen] = useState(false)
  const [selectedSubmission, setSelectedSubmission] = useState<CompletedChallenge | null>(null)
  const { isAuthenticated } = useAuth()
  const { getSetting, setSetting } = useLocalSettings()
  const { getSetting: getUserSetting, setSetting: setUserSetting } = useUserSettings()
  const { showJoinGammaCta } = useShowJoinGamma()
  const navigate = useNavigate()
  const { t } = useTar()
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [showBetaOfferDialog, setShowBetaOfferDialog] = useState(false)
  const [isJoiningGamma, setIsJoiningGamma] = useState(false)
  // Seeded by the workspace with the message the submit request returned; the card's lightbulb reopens it later.
  const [message, setMessage] = useAtom(postSubmitMessageAtom)
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null)

  const [taskFilter, setTaskFilter] = useState<string | undefined>(undefined)
  const [attemptFilter, setAttemptFilter] = useState<AttemptFilter | undefined>(undefined)

  // Calculate attempt numbers + totals for each challenge based on taskId and endTime.
  // `attemptMap` → 1-based position of this attempt; `totalAttemptsByTaskId` → count of attempts per task.
  const { attemptMap, totalAttemptsByTaskId } = useMemo(() => {
    const map = new Map<string, number>()
    const totals = new Map<string, number>()

    const challengesByTaskId = new Map<string, CompletedChallenge[]>()
    challenges.forEach(challenge => {
      const taskId = challenge.task.id
      if (!challengesByTaskId.has(taskId)) {
        challengesByTaskId.set(taskId, [])
      }
      const existingChallenges = challengesByTaskId.get(taskId)
      if (existingChallenges) {
        existingChallenges.push(challenge)
      }
    })

    challengesByTaskId.forEach((taskChallenges, taskId) => {
      const sorted = [...taskChallenges].sort((a, b) => {
        const timeA = a.challenge.endTime ? new Date(a.challenge.endTime).getTime() : 0
        const timeB = b.challenge.endTime ? new Date(b.challenge.endTime).getTime() : 0
        return timeA - timeB
      })
      sorted.forEach((challenge, index) => {
        map.set(challenge.challenge.id, index + 1)
      })
      totals.set(taskId, sorted.length)
    })

    return { attemptMap: map, totalAttemptsByTaskId: totals }
  }, [challenges])

  const taskOptions = useMemo(() => {
    const byId = new Map<string, string>()
    challenges.forEach(c => {
      if (!byId.has(c.task.id)) byId.set(c.task.id, c.task.name)
    })
    const tasks = Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return [{ value: 'any', label: t('challenges.taskAny') }, ...tasks]
  }, [challenges, t])

  const attemptOptions = useMemo<{ value: string; label: string }[]>(() => [
    { value: 'any', label: t('challenges.attemptAny') },
    { value: 'first', label: t('challenges.attemptFirst') },
    { value: 'second', label: t('challenges.attemptSecond') },
    { value: 'third', label: t('challenges.attemptThird') },
    { value: 'fourth', label: t('challenges.attemptFourthPlus') },
    { value: 'last', label: t('challenges.attemptLast') },
  ], [t])

  const filteredSortedChallenges = useMemo(() => {
    const filtered = challenges.filter(c => {
      if (taskFilter && c.task.id !== taskFilter) return false
      if (attemptFilter) {
        const n = attemptMap.get(c.challenge.id) ?? 1
        const total = totalAttemptsByTaskId.get(c.task.id) ?? 1
        if (attemptFilter === 'first' && n !== 1) return false
        if (attemptFilter === 'second' && n !== 2) return false
        if (attemptFilter === 'third' && n !== 3) return false
        if (attemptFilter === 'fourth' && n < 4) return false
        if (attemptFilter === 'last' && n !== total) return false
      }
      return true
    })
    return filtered.sort((a, b) => {
      const timeA = a.challenge.endTime ? new Date(a.challenge.endTime).getTime() : 0
      const timeB = b.challenge.endTime ? new Date(b.challenge.endTime).getTime() : 0
      return timeB - timeA
    })
  }, [challenges, taskFilter, attemptFilter, attemptMap, totalAttemptsByTaskId])

  const fetchChallenges = useCallback(async (skipLoading = false) => {
    if (isAuthenticated) {
      try {
        if (!skipLoading) {
          setIsLoading(true)
        }
        const challengesResponse = await apis.challengeService.getFinishedChallenges()
        setChallenges(challengesResponse.data?.map((challenge: ChallengeService.ChallengeInfo) => ({
          ...challenge,
          estimatedTime: '30' // Default estimated time
        })) || [])
      } catch (error) {
        console.error('Error fetching challenges:', error)
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current)
          refreshIntervalRef.current = null
        }
      } finally {
        setIsLoading(false)
      }
    } else {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  useEffectAsync(async () => {
    await fetchChallenges()
  }, [fetchChallenges])

  const handleShowMessage = (c: CompletedChallenge) =>
    setMessage({ challengeId: c.challenge.id, organizationId: c.challenge.organizationId, message: c.challenge.submitDisclaimer ?? '' })

  const handleMessageClosed = () => {
    const id = message?.challengeId ?? null
    setMessage(null)
    if (id !== null) {
      setHighlightedMessageId(id)
      window.setTimeout(() => setHighlightedMessageId((current) => (current === id ? null : current)), 1500)
    }
  }

  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setShowLoading(true)
      }, 256)
      return () => clearTimeout(timeout)
    }
    setShowLoading(false)
  }, [isLoading])

  useEffect(() => {
    if (isAuthenticated) {
      const dismissed = getUserSetting('dismissedCandidateBetaOffer')
      if (dismissed !== true && showJoinGammaCta) {
        setShowBetaOfferDialog(true)
      }
    }
  }, [isAuthenticated, getUserSetting, showJoinGammaCta])

  const handleCloseBetaOfferDialog = async () => {
    setShowBetaOfferDialog(false)
    await setUserSetting('dismissedCandidateBetaOffer', true)
  }

  const handleJoinGamma = async () => {
    if (isJoiningGamma) return
    setIsJoiningGamma(true)
    try {
      await apis.userService.joinGammaPost()
      setShowBetaOfferDialog(false)
      await setUserSetting('dismissedCandidateBetaOffer', true)
      navigate({ to: '/challenges/available' })
    } catch (error) {
      console.error('Failed to join Gamma:', error)
      toast.error('Failed to join. Please try again.')
    } finally {
      setIsJoiningGamma(false)
    }
  }

  const handleDialogOpenChange = async (open: boolean) => {
    if (!open) {
      await setUserSetting('dismissedCandidateBetaOffer', true)
    }
    setShowBetaOfferDialog(open)
  }

  // Set up interval to refresh data when there are unprocessed challenges
  useEffect(() => {
    const hasUnprocessedChallenges = challenges.some(
      challenge => challenge.challenge.feedbackStatus !== ChallengeService.FeedbackStatusEnum.Processed
        && !challenge.challenge.assignmentId
    )

    if (hasUnprocessedChallenges && isAuthenticated) {
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }

      // Set up new interval
      refreshIntervalRef.current = setInterval(() => {
        fetchChallenges(true)
      }, 5060) // 5 seconds and a bit
    } else {
      // Clear interval when all challenges are processed or user is not authenticated
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }

    // Cleanup interval on component unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }
  }, [challenges, isAuthenticated])

  const handleShowResults = (challenge: CompletedChallenge) => {
    setSelectedChallenge(challenge)
    setInitialFeedbackViewMode('feedback')
    setIsFeedbackDialogOpen(true)
  }

  const handleCloseDialog = () => {
    const challengeToRate = selectedChallenge
    setIsFeedbackDialogOpen(false)
    setSelectedChallenge(null)

    const feedbackToasterDismissed = getSetting('feedbackToasterDismissed')
    if (!challengeToRate?.challenge.userFeedback && !feedbackToasterDismissed) {
      const dismissToaster = () => {
        setSetting('feedbackToasterDismissed', true)
      }

      const toastId = toast(t('userFeedback.feedbackReminderTitle'), {
        description: t('userFeedback.feedbackReminderDescription'),
        duration: Infinity,
        action: {
          label: t('userFeedback.evaluate'),
          onClick: () => {
            dismissToaster()
            setSelectedChallenge(challengeToRate)
            setInitialFeedbackViewMode('submitFeedback')
            setIsFeedbackDialogOpen(true)
          }
        },
        cancel: {
          label: t('common.dismiss'),
          onClick: () => {
            dismissToaster()
            toast.dismiss(toastId)
          }
        }
      })
    }
  }

  const handleShowSubmission = (c: CompletedChallenge) => {
    setSelectedSubmission(c)
    setIsSolutionDialogOpen(true)
  }

  // TODO: fetch user's public achievements and populate this map (list endpoint not yet available)
  const publicMap = useMemo(() => new Map<string, boolean>(), [])

  return (
    <>
      <Dialog open={showBetaOfferDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Join the closed beta</DialogTitle>
          </DialogHeader>
          <div className="text-foreground text-sm leading-relaxed mb-4">
            This task doesn’t include feedback.<br />
            But XP Lab practice challenges do - with real, actionable feedback.<br />
            <br />
            You’re seeing this because XP Lab is currently in closed beta: early access, evolving challenges, and direct influence on what we build next.<br />
            <br />
            Free to join. No credit card. No commitment.
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={handleJoinGamma} disabled={isJoiningGamma}>
              Join
            </Button>
            <Button onClick={handleCloseBetaOfferDialog} variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex flex-1 w-full justify-center">
        <div className="flex-1 max-w-6xl space-y-6 mt-4">
          {/* Header Section */}
          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-foreground">{t('challenges.completedChallenges')}</h1>

            {/* Separator */}
            <div className="h-px bg-border" />

            {/* Description */}
            <p className="text-muted-foreground text-lg leading-relaxed">
              {t('challenges.completedDescription')}
            </p>
          </div>

          {/* Filters */}
          {(isLoading || challenges.length > 0) && (
            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <div className="flex-1 md:max-w-xs">
                <SinglePick
                  options={taskOptions}
                  selected={taskFilter ?? 'any'}
                  onChange={(v) => setTaskFilter(v === 'any' || v == null ? undefined : v)}
                  searchable={false}
                  labels={{
                    placeholder: t('challenges.taskAny'),
                    emptyMessage: t('common.noResults'),
                  }}
                />
              </div>
              <div className="flex-1 md:max-w-[180px]">
                <SinglePick
                  options={attemptOptions}
                  selected={attemptFilter ?? 'any'}
                  onChange={(v) => setAttemptFilter(v === 'any' || v == null ? undefined : (v as AttemptFilter))}
                  searchable={false}
                  labels={{
                    placeholder: t('challenges.attemptAny'),
                    emptyMessage: t('common.noResults'),
                  }}
                />
              </div>
            </div>
          )}

          {/* Challenge Cards Grid */}
          <div className="grid grid-cols-1 gap-6">
            {showLoading && isLoading ? (
              <CompletedSubmissionCardSkeleton count={4} />
            ) : (
              filteredSortedChallenges.map((completedChallenge) => (
                <CompletedSubmissionCard
                  key={completedChallenge.challenge.id}
                  completedChallenge={completedChallenge}
                  attemptNumber={attemptMap.get(completedChallenge.challenge.id) ?? 1}
                  totalAttempts={totalAttemptsByTaskId.get(completedChallenge.task.id) ?? 1}
                  isPublic={publicMap.get(completedChallenge.challenge.id) ?? false}
                  onShowResults={handleShowResults}
                  onShowSubmission={handleShowSubmission}
                  onShowMessage={handleShowMessage}
                  highlightMessageButton={highlightedMessageId === completedChallenge.challenge.id}
                />
              ))
            )}
          </div>

          {/* Empty State */}
          {!isLoading && challenges.length === 0 && (
            <>
              {!isAuthenticated ? (
                <LoginRequiredEmptyState />
              ) : (
                <NoDataEmptyState
                  title={t('challenges.noCompletedYet')}
                  description={t('challenges.noCompletedDescription')}
                  buttonText={t('challenges.startFirstChallenge')}
                  onButtonClick={() => navigate({ to: '/challenges/available' })}
                  icon={CheckCircleIcon}
                />
              )}
            </>
          )}

          {/* Filtered empty state */}
          {!isLoading && challenges.length > 0 && filteredSortedChallenges.length === 0 && (
            <NoDataEmptyState
              title={t('challenges.noFilteredMatches')}
              description={t('challenges.noFilteredMatchesDescription')}
              buttonText={t('challenges.browseAvailableChallenges')}
              onButtonClick={() => navigate({ to: '/challenges/available' })}
              icon={FunnelIcon}
            />
          )}
        </div>

        <PostSubmitMessageDialog message={message} onClosed={handleMessageClosed} />

        {/* Feedback Overview Dialog */}
        {selectedChallenge && <FeedbackOverview
          isOpen={isFeedbackDialogOpen}
          onClose={handleCloseDialog}
          challenge={selectedChallenge}
          initialViewMode={initialFeedbackViewMode}
          onShareAchievement={(c) => {
            trackAchievement('Achievement Share CTA Clicked', {
              challengeSlug: c.challenge.slug,
              taskName: c.task.name,
              source: 'feedback_dialog',
            })
            navigate({ to: '/my-shares/$challengeSlug', params: { challengeSlug: c.challenge.slug } })
          }}
        />}

        {/* Submission Dialog (unreleased assignments) */}
        <Dialog open={isSolutionDialogOpen} onOpenChange={setIsSolutionDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>{t('challenges.submission')}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-auto border border-border rounded-md bg-background min-h-[300px]">
              {selectedSubmission?.challenge.workState?.chosenWeapon === ChallengeService.TaskWeaponsOfChoiceEnum.Python ? (
                <ReadOnlyPythonNotebook
                  notebook={selectedSubmission.challenge.workState?.jNotebook as INotebookContent | undefined}
                />
              ) : (
                <WysiwygEditor
                  readOnly
                  content={selectedSubmission?.challenge.workState?.solution || ''}
                  startingCharts={selectedSubmission?.challenge.workState?.charts || {}}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  )
}
